import { z } from "zod";
import { IsoTimestamp, MatchId, NightId, PlayerId } from "./ids.js";
import { LinkIngestMethod, Match, MatchScore, MatchState, VarVerdict } from "./match.js";

// ─────────────────────────────────────────────────────────────────────────────
// LOOP 1 API CONTRACTS v0.3 — the match-engine service surface.
// AUTH (D-010): every player endpoint takes `Authorization: Bearer <supabase_jwt>`
// (the D-009 token) — stateless verification with SUPABASE_JWT_SECRET, sub =
// player_id. DUGOUT endpoints use the service role + operator identity header.
// Errors are ApiError with documented codes per endpoint.
// ─────────────────────────────────────────────────────────────────────────────

/** Public identity — SAFE columns only, backed by the player_identity view
 *  (migration 0002). Never carries msisdn_hash/coins/confidence.
 *  v0.4 (centre CCR): GET /me → PlayerIdentity — the caller's own identity for
 *  the takeover/pinned "YOU" surfaces. Same shape, no envelope. */
export const PlayerIdentity = z.object({
  id: PlayerId,
  gamertag: z.string(),
  badge_id: z.string(),
  avatar_id: z.string(),
  founder: z.boolean(),
  division: z.number().int().min(1),
});
export type PlayerIdentity = z.infer<typeof PlayerIdentity>;

// ── Feed (MATCH POINT §5.1 — the card stack) ─────────────────────────────────
export const FeedCard = z.object({
  match_id: MatchId,
  night_id: NightId,
  host: PlayerIdentity,
  card_live_at: IsoTimestamp,
  expires_at: IsoTimestamp, // card_live_at + 5:00 (the ring; EA window buffer is the design)
  // v0.4 (feed CCR): the §5.9 host-economy values the card's WIN chip renders —
  // server-supplied (engine config), never hardcoded client-side (Rule 1).
  // nullish = additive: engine returns it as of the Loop 1 close.
  reward: z
    .object({
      host_coins_create: z.number().int(), // create-grant (+5 default, unjoined-capped)
      host_coins_claimed: z.number().int(), // join-grant (+5 default, uncapped)
    })
    .nullish(),
});
export type FeedCard = z.infer<typeof FeedCard>;

export const FeedResponse = z.object({
  cards: z.array(FeedCard), // expiring-soonest first; viewer's recent opponent filtered (§5.4b R2)
  total_live: z.number().int().min(0), // excludes cards hidden for THIS viewer
  /**
   * YOUR own cards on the board, soonest-expiring first.
   *
   * Was `your_hosted: FeedCard | null` back when a player could only have one.
   * A card is an offer now (BK, 14 Aug) and a player may post as many as they
   * like, so this is a list — a single nullable field would have silently shown
   * them one of several and let them think the others never landed.
   */
  your_cards: z.array(FeedCard),
});
export type FeedResponse = z.infer<typeof FeedResponse>;

// ── Create / arm (OPEN PLAY wizard W3, share relay §5.5) ─────────────────────
// Server parses the EA share payload — ONE parser, server-side truth. Errors:
// max_hosted · night_closed · night_unknown · bad_link · not_ready.
export const CreateMatchRequest = z.object({
  night_code: z.string().min(1),
  shared_text: z.string().min(1), // raw share-sheet/paste/typed payload
  method: LinkIngestMethod,
});
export type CreateMatchRequest = z.infer<typeof CreateMatchRequest>;

/** v0.4 ratification (contract-change request #3): THE `{match: Match}`
 *  envelope the engine answers state-moving writes with — create, claim (plus
 *  ea_join_url, see ClaimResponse), withdraw, link-opened, result, verify and
 *  release all return this shape. Clients re-fetch GET /match/{id} when they
 *  need the full MatchStateResponse truth. */
export const MatchEnvelope = z.object({ match: Match });
export type MatchEnvelope = z.infer<typeof MatchEnvelope>;

export const CreateMatchResponse = z.object({ match: Match });
export type CreateMatchResponse = z.infer<typeof CreateMatchResponse>;

/**
 * POST /match/no-show — the player who turned up reports that the other did not.
 *
 * Carries only the match id. WHO was absent is derived server-side from
 * host_opened_at / joiner_opened_at rather than claimed by the reporter — a
 * request that let a player name the no-show would let them name the wrong one,
 * and this action takes points off whoever is named.
 *
 * The screenshot is filed separately through the evidence pipeline and read by
 * VAR; it is not a precondition here, because the arrival columns are the
 * authoritative record of who came and the picture only corroborates it.
 */
export const NoShowRequest = z.object({ match_id: MatchId });
export type NoShowRequest = z.infer<typeof NoShowRequest>;

export const WithdrawRequest = z.object({ match_id: MatchId });
export type WithdrawRequest = z.infer<typeof WithdrawRequest>;

/** v0.4 (feed CCR — PULLED UP escape hatch): POST /match/release — the HOST
 *  releases a CLAIMED match pre-kickoff. Engine rules (ARCHITECT ruling):
 *  CLAIMED → VOIDED via match_voided, reason_code 'host_released'; the joiner
 *  is freed with the §PULLED-UP consolation coin grant; the host takes a
 *  confidence_changed hit (config 'confidence.release_delta', default −2 —
 *  "dents your Confidence"). Returns MatchEnvelope.
 *  Errors: match_unknown · not_host · not_releasable (state ≠ CLAIMED). */
export const ReleaseRequest = z.object({ match_id: MatchId });
export type ReleaseRequest = z.infer<typeof ReleaseRequest>;

// ── Claim (atomic, first tap wins — §5.1) ────────────────────────────────────
// Errors: snapped (already claimed) · own_card · recent_opponent · expired.
export const ClaimRequest = z.object({ match_id: MatchId });
export type ClaimRequest = z.infer<typeof ClaimRequest>;

export const ClaimResponse = z.object({
  match: Match,
  ea_join_url: z.string().url(), // revealed ONLY to the claimer, at claim time
});
export type ClaimResponse = z.infer<typeof ClaimResponse>;

export const LinkOpenedRequest = z.object({ match_id: MatchId });
export type LinkOpenedRequest = z.infer<typeof LinkOpenedRequest>;

// ── Result / verify / dispute (§5.6, FAIR RESULT tier 1) ─────────────────────
export const SubmitResultRequest = z.object({
  match_id: MatchId,
  score: MatchScore,
  screenshot_sha256: z.string().length(64).nullable(), // evidence attaches async
});
export type SubmitResultRequest = z.infer<typeof SubmitResultRequest>;

export const VerifyResultRequest = z.object({
  match_id: MatchId,
  action: z.enum(["verify", "accept_theirs", "correct_mine", "dispute"]),
  corrected_score: MatchScore.optional(), // correct_mine only
  note: z.string().max(140).optional(), // dispute only ("Evidence talks.")
});
export type VerifyResultRequest = z.infer<typeof VerifyResultRequest>;

/** GET /match/{id} — the Match Centre's single truth. */
export const MatchStateResponse = z.object({
  match: Match,
  you_are: z.enum(["host", "joiner"]),
  opponent: PlayerIdentity.nullable(),
  your_submission: MatchScore.nullable(),
  their_submission_present: z.boolean(), // never their score before you submit? — no: verify screen shows it
  their_submission: MatchScore.nullable(),
  ea_join_url: z.string().url().nullable(), // participants only, live states only
  // v0.4 (centre CCR #1/#2 — UPSTAIRS: the clock is a promise, server-anchored).
  // nullish = additive: engine returns them as it wires the anchors (Loop 1 close).
  me: PlayerIdentity.nullish(), // your own identity for the VS home side
  lobby_expires_at: IsoTimestamp.nullish(),
  auto_confirm_at: IsoTimestamp.nullish(),
  disputed_at: IsoTimestamp.nullish(),
});
export type MatchStateResponse = z.infer<typeof MatchStateResponse>;

// ── Match history (SCREEN 05's verified CTA destination) ─────────────────────
//
// The customer flow lists "Match history & ELO" as KEPT for 90 days on a lapsed
// subscription, beside coins — a named player asset the product promises to
// preserve. Something we promise to keep has to be something the player can look
// at, so it is a read, not a derivation the client does over the feed.

/** How the result became final. More useful than a `verified: boolean`, and
 *  fully derivable from data we already hold: a clean loop leaves outcome_reason
 *  null, a dispute verdict writes a reason_code, a walkover has its own state. */
export const MatchHistoryVia = z.enum(["verified", "var", "walkover", "no_show"]);
export type MatchHistoryVia = z.infer<typeof MatchHistoryVia>;

export const MatchHistoryRow = z.object({
  match_id: MatchId,
  night_id: NightId,
  you_are: z.enum(["host", "joiner"]),
  opponent: PlayerIdentity.nullable(), // null only if the opponent record is unreadable
  /** Viewer-relative, and deliberately SERVER-derived. Everywhere else the
   *  client derives W/D/L from the score (outcomeFor), but a walkover has no
   *  score — its winner is `awarded_to`. Two different derivations for one
   *  column is how a row ends up labelled "L" on a match the player won, so the
   *  server settles it once and the row arrives self-describing.
   *
   *  NULL on a double no-show: neither player arrived, so there is no result to
   *  report for either of them. BK asked for those to be listed rather than
   *  deleted — listed, but not scored. Forcing them into W/D/L would put a
   *  verdict on a match nobody turned up to. */
  outcome: z.enum(["W", "D", "L"]).nullable(),
  score: MatchScore.nullable(), // null on a walkover: nobody kicked off
  via: MatchHistoryVia,
  resolved_at: IsoTimestamp,
});
export type MatchHistoryRow = z.infer<typeof MatchHistoryRow>;

/** GET /match/history — the player's resolved matches, newest first.
 *
 *  COMPLETE, WALKOVER, and the double-no-show. EXPIRED/WITHDRAWN stay out — an
 *  expired or withdrawn card never became a match — as does a no-fault VOID.
 *
 *  The no-show is listed (BK: "we can just list it as no-show") but NOT scored:
 *  its outcome is null and it is absent from played/won/drawn/lost. Nobody
 *  played it, so counting it would inflate a record with a match that never
 *  kicked off. */
export const MatchHistoryResponse = z.object({
  rows: z.array(MatchHistoryRow),
  /** Server-computed so every surface agrees, and so the record line does not
   *  disagree with the rows when the list is truncated. */
  played: z.number().int().min(0),
  won: z.number().int().min(0),
  drawn: z.number().int().min(0),
  lost: z.number().int().min(0),
});
export type MatchHistoryResponse = z.infer<typeof MatchHistoryResponse>;

/**
 * GET /match/active — the caller's one live match, or null.
 *
 * The mirror of /match/history: that one answers "what have I finished", this
 * one answers "what am I in the middle of". Nothing could answer the second
 * question before, which is how BK's 28 Jul test ended with a played match and
 * nowhere to report it — the RESULT tab had no way to know a match was waiting.
 *
 * Server-side rather than a remembered id on the device, because a phone that
 * was restarted, ran out of storage, or opened the app in another browser must
 * still be able to find the match it owes a score to.
 *
 * At most one: MAX_HOSTED is 1 and a player can hold one claim, so a second
 * live match would itself be the bug. Newest wins if the invariant ever breaks,
 * so the answer is the match they were most recently actually playing.
 */
export const ActiveMatchResponse = z.object({
  match: Match.nullable(),
  /**
   * Has THIS player already filed their score? Carried here because the caller
   * cannot infer it: RESULT_PENDING means one side has reported and says
   * nothing about which. Without this the RESULT tab would tell the player who
   * had just done the right thing to go and do it again.
   *
   * False when there is no match — nothing is owed by nobody.
   */
  you_submitted: z.boolean(),
  /**
   * Has THIS player opened the EA lobby link?
   *
   * Carried because the caller cannot infer it: `match.state` is SHARED and
   * flips to IN_GAME on the FIRST arrival, so a player who never left the
   * match centre looks identical to one who played. The result lock reads
   * this — without it, it pins a player to a screen demanding a score for a
   * game they were never let into.
   *
   * False when there is no match.
   */
  you_arrived: z.boolean(),
  /**
   * Is your opponent's score sitting there waiting for YOUR word?
   *
   * VERIFICATION with their submission in and yours absent. Carried because
   * the caller cannot infer it — `match.state` says a score has been filed and
   * nothing about by whom.
   *
   * This exists because of what it cost on 13 Aug. THEWIZARD filed 0–1 with a
   * screenshot at 10:53:28. SERGIOAG came back to the app, was shown nothing,
   * and the score auto-confirmed against them at 10:58:39 — five minutes and
   * eleven seconds later, the AUTO_CONFIRM window exactly. The result lock only
   * ever knew about owing a SCORE, so the moment the opponent submitted, the
   * match left RESULT_PENDING and the lock let go of the one player who still
   * had something to do. A verification is an obligation with a deadline and a
   * cost attached; it has to be as loud as the score itself.
   *
   * False when there is no match.
   */
  awaiting_your_verify: z.boolean(),
});
export type ActiveMatchResponse = z.infer<typeof ActiveMatchResponse>;

// ── LIVE TICKER (BK, 27 Jul — global chrome, feed + locker + table) ─────────
//
// "This live ticker must take all player actions … all to keep it busy and run
//  it as a ticker of activity on the platform."
//
// Derived from Postgres in a service that already holds the database, NOT from
// the event stream: ingest has no DB access at all (contracts + zod only), so it
// cannot resolve a player_id into the gamertag every line needs. Reading state
// also means a renamed player shows their CURRENT name.

export const TickerKind = z.enum([
  "match_created",
  "match_completed",
  "profile_created",
  "profile_updated",
  "trophy_earned",
  "reward",
  "chat",
  "climb",
]);
export type TickerKind = z.infer<typeof TickerKind>;

export const TickerRow = z.object({
  kind: TickerKind,
  /** Who did it. The whole point of the strip — a line with no name is noise. */
  gamertag: z.string(),
  /** The second party, when there is one (a match has two). */
  opponent_gamertag: z.string().nullable(),
  /** Rendered detail: "3–0", "Debut Trophy", "50 FC Coins". Null where the kind
   *  says everything ("joined the night"). Never free prose from a player. */
  detail: z.string().nullable(),
  at: IsoTimestamp,
});
export type TickerRow = z.infer<typeof TickerRow>;

/** GET /ticker — newest first, already merged across every source.
 *  An EMPTY list is a real answer: a quiet platform gets a quiet strip, never
 *  padding. The client renders nothing rather than inventing activity. */
export const TickerResponse = z.object({ rows: z.array(TickerRow) });
export type TickerResponse = z.infer<typeof TickerResponse>;

// ── Match chat (TOUCHLINE A2 — presets first, on the record) ─────────────────
export const ChatPostRequest = z.object({
  match_id: MatchId,
  preset: z.string().nullable(),
  body: z.string().min(1).max(280).nullable(),
});
export type ChatPostRequest = z.infer<typeof ChatPostRequest>;

export const ChatMessageOut = z.object({
  id: z.string().uuid(),
  match_id: MatchId,
  player: PlayerIdentity,
  preset: z.string().nullable(),
  body: z.string().nullable(),
  created_at: IsoTimestamp,
  /**
   * Hidden FOR THE REPORTER after they report it (SCREEN 05 §Report:
   * "It's hidden for you immediately and goes to the ops queue").
   *
   * Reporter-scoped, not global: the message stays visible to everyone else
   * and to ops until a human rules on it. One player cannot silence another by
   * tapping report — that would make the report button a weapon rather than a
   * safety valve. Mirrors the Night Room's RoomMessage.hidden.
   */
  hidden: z.boolean().default(false),
});
export type ChatMessageOut = z.infer<typeof ChatMessageOut>;

/**
 * POST /match/chat/report — SCREEN 05 §Report.
 *
 * Same shape as the Night Room's ReportMessageRequest so the two report paths
 * stay one concept. Match chat and room chat are different stores with
 * different id spaces, which is why this cannot simply reuse that endpoint.
 */
export const ReportChatMessageRequest = z.object({
  /** Scopes the lookup — the client always has it on this screen, and it keeps
   *  the server from scanning chat_messages by id alone. */
  match_id: MatchId,
  message_id: z.string().uuid(),
  reason_code: z.string().min(1),
});
export type ReportChatMessageRequest = z.infer<typeof ReportChatMessageRequest>;

/** v0.4 ratification (PROOFER BREAK-1): GET /match/{id}/chat answers THIS
 *  envelope — the engine's shape wins; the client unwraps. */
export const ChatListResponse = z.object({ messages: z.array(ChatMessageOut) });
export type ChatListResponse = z.infer<typeof ChatListResponse>;

// ── VAR Review Room (UPSTAIRS V2 — the VAR home) ─────────────────────────────
// GET /var/room → the caller's own matches sorted into the brief's three
// stacks. Operator-side /var/queue is a DIFFERENT surface (every player's
// disputes, DUGOUT-only) — this one is player-scoped and read-only.
export const VarRoomStack = z.enum(["needs_you", "checking", "decided"]);
export type VarRoomStack = z.infer<typeof VarRoomStack>;

export const VarRoomRow = z.object({
  match_id: MatchId,
  stack: VarRoomStack,
  opponent: PlayerIdentity.nullable(),
  state: MatchState,
  /** The settled/submitted score as the viewer reads it, their side first. */
  your_goals: z.number().int().nullable(),
  their_goals: z.number().int().nullable(),
  /** needs_you: when the opponent's submission auto-confirms if untouched. */
  auto_confirm_at: IsoTimestamp.nullish(),
  /** checking: when the dispute opened (drives the elapsed clock). */
  disputed_at: IsoTimestamp.nullish(),
  /** decided: the V7 stamp this match landed on, plus its reason code. */
  verdict: VarVerdict.nullish(),
  outcome_reason: z.string().nullish(),
  resolved_at: IsoTimestamp.nullish(),
});
export type VarRoomRow = z.infer<typeof VarRoomRow>;

export const VarRoomResponse = z.object({
  rows: z.array(VarRoomRow),
  /** V1 menu-badge state: volt dot when any needs_you, steel when any checking. */
  needs_you_count: z.number().int().min(0),
  checking_count: z.number().int().min(0),
});
export type VarRoomResponse = z.infer<typeof VarRoomResponse>;

// ── Evidence (FAIR RESULT §3 — compressed client-side, async upload) ─────────
export const EvidenceUploadRequest = z.object({
  match_id: MatchId,
  sha256: z.string().length(64),
  phash: z.string().min(8),
  width: z.number().int().min(1),
  height: z.number().int().min(1),
  content_base64: z.string().min(1), // ≤600KB post-compression contract (client enforces)
});
export type EvidenceUploadRequest = z.infer<typeof EvidenceUploadRequest>;

// ── Leaderboard v1 (THE TABLE items 1–3) ─────────────────────────────────────
export const LeaderboardRow = z.object({
  rank: z.number().int().min(1),
  player: PlayerIdentity,
  points: z.number().int(),
  /** vs the last standings tick — ▲2 / ▼1. NULL until a player has been ticked
   *  at least once: "never measured" and "held station" are different facts and
   *  only one of them earns an arrow. Real as of migration 0008; hardcoded 0
   *  before that, which is why the comp's arrows never moved. */
  delta: z.number().int().nullable(),
  /** Movement across the WHOLE night (first_rank - rank) — the CLIMBERS tab.
   *  A different question from `delta`, so it gets its own anchor. */
  climb: z.number().int().nullable(),
  in_money: z.boolean(),
});
export type LeaderboardRow = z.infer<typeof LeaderboardRow>;

export const LeaderboardResponse = z.object({
  night_id: NightId,
  rows: z.array(LeaderboardRow),
  you: LeaderboardRow.nullable(),
  money_line_rank: z.number().int().nullable(), // the gold rule's cutoff
});
export type LeaderboardResponse = z.infer<typeof LeaderboardResponse>;

// ── DUGOUT VAR console v1 (assist-mode; keys 1–5, reason-coded) ──────────────
export const VarCaseSummary = z.object({
  match_id: MatchId,
  night_id: NightId,
  raised_at: IsoTimestamp,
  host: PlayerIdentity,
  joiner: PlayerIdentity,
  host_score: MatchScore.nullable(),
  joiner_score: MatchScore.nullable(),
  sla_ms: z.number().int().min(0),
});
export type VarCaseSummary = z.infer<typeof VarCaseSummary>;

// v0.4: shared reason vocabulary — every verdict is a training label (FAIR RESULT §5.3).
export const VarReasonCode = z.enum([
  "score_evidence_clear",
  "no_show_ledger",
  "presence_ambiguous",
  "fabrication_finding",
  "procedural",
]);
export type VarReasonCode = z.infer<typeof VarReasonCode>;

export const VarVerdictRequest = z.object({
  match_id: MatchId,
  verdict: VarVerdict,
  reason_code: VarReasonCode, // structured picklist — never free-prose-only
  note: z.string().max(500).optional(),
  // WALKOVER: DUGOUT names the winner explicitly; omitted ⇒ engine default (the disputer).
  awarded_to: PlayerId.optional(),
});
export type VarVerdictRequest = z.infer<typeof VarVerdictRequest>;

export const VarVerdictResponse = z.object({
  ok: z.literal(true),
  match_state: z.string(), // the resulting MatchState — the ack names the outcome
});
export type VarVerdictResponse = z.infer<typeof VarVerdictResponse>;

export const VarQueueResponse = z.object({
  cases: z.array(VarCaseSummary),
  agent_agreement_pct: z.number().min(0).max(100).nullable(), // null until Loop 4's agent runs
});
export type VarQueueResponse = z.infer<typeof VarQueueResponse>;

// GET /dugout/var/case/{match_id} — the case room's full record (VAR CCR #1).
export const VarLedgerLine = z.object({
  at: IsoTimestamp,
  event_type: z.string(),
  actor: z.string().nullable(), // gamertag or 'system'/'dugout:{operator}'
  detail: z.string().nullable(),
});
export type VarLedgerLine = z.infer<typeof VarLedgerLine>;

export const VarCaseDetail = z.object({
  summary: VarCaseSummary,
  ledger: z.array(VarLedgerLine),
  dispute_note: z.string().nullable(),
});
export type VarCaseDetail = z.infer<typeof VarCaseDetail>;

export const NightOpenRequest = z.object({ night_code: z.string().min(1) });
export type NightOpenRequest = z.infer<typeof NightOpenRequest>;
